<template>
  <div class="my-bookings-page">
    <v-container>
      <!-- Page Header -->
      <div class="page-header mb-8">
        <h1 class="page-title">My Bookings</h1>
        <p class="page-subtitle">Track and manage your service requests</p>
      </div>

      <!-- Global Loading State -->
      <global-loader v-if="loading" message="Fetching your bookings..." />

      <!-- Empty State -->
      <div v-else-if="bookings.length === 0" class="empty-state">
        <v-icon size="64" color="#e0e0e0">mdi-calendar-blank</v-icon>
        <h2 class="empty-title">No bookings found</h2>
        <p class="empty-text">You haven't scheduled any services yet.</p>
        <v-btn
          color="#f46523"
          dark
          depressed
          rounded
          class="mt-4 px-6"
          @click="$router.push(servicesPath)"
        >
          Explore Services
        </v-btn>
      </div>

      <!-- Bookings List -->
      <div v-else class="bookings-list">
        <v-row>
          <v-col
            v-for="booking in bookings"
            :key="booking._id"
            cols="12"
            md="6"
            lg="4"
          >
            <v-card class="booking-card" outlined>
              <div class="booking-status-badge" :class="booking.status.toLowerCase()">
                {{ booking.status }}
              </div>
              
              <v-card-text class="pa-6 pt-10">
                <div class="d-flex align-center mb-4">
                  <div class="service-icon-wrapper mr-4">
                    <v-img
                      v-if="getCartItem(booking).image"
                      :src="getCartItem(booking).image"
                      width="44"
                      height="44"
                      contain
                    />
                    <v-icon v-else color="#f46523">mdi-tools</v-icon>
                  </div>
                  <div class="service-details">
                    <h3 class="booking-service-name">{{ getCartItem(booking).title || 'Service' }}</h3>
                    <div class="booking-id">#{{ booking._id.slice(-6).toUpperCase() }}</div>
                  </div>
                </div>

                <v-divider class="mb-4" />

                <div class="booking-info-row">
                  <v-icon small color="#666" class="mr-2">mdi-calendar</v-icon>
                  <span>{{ formatDateTime(getCartItem(booking).requested_date_time) }}</span>
                </div>
                
                <!-- Transaction Info -->
                <div v-if="booking.transaction_info" class="booking-info-row mt-2">
                  <v-icon small color="#666" class="mr-2">mdi-credit-card-outline</v-icon>
                  <span class="text-capitalize">
                    {{ booking.transaction_info.payment_method }} - 
                    <span :class="booking.transaction_info.payment_status === 'pending' ? 'status-pending' : 'status-success'">
                      {{ booking.transaction_info.payment_status }}
                    </span>
                  </span>
                </div>

                <div v-if="booking.assigned_partner" class="booking-info-row mt-2">
                  <v-icon small color="#666" class="mr-2">mdi-account-star</v-icon>
                  <span>{{ booking.assigned_partner.name }}</span>
                </div>

                <!-- Address Info -->
                <div v-if="getAddress(booking)" class="booking-info-row mt-2 pb-2 align-start">
                  <v-icon small color="#666" class="mr-2 mt-1">mdi-map-marker</v-icon>
                  <div class="address-container">
                    <div class="address-title">{{ getAddress(booking).title }}</div>
                    <div class="address-text">{{ getAddress(booking).address_line_1 }}, {{ getAddress(booking).city }}</div>
                  </div>
                </div>
              </v-card-text>

              <v-divider />

              <v-card-actions class="pa-4">
                <v-spacer />
                <v-btn
                  text
                  color="#f46523"
                  class="action-btn"
                  small
                  @click="goToBookingDetails(booking._id)"
                >
                  View Details
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>

        <!-- Pagination -->
        <div class="pagination-wrapper mt-8">
          <v-pagination
            v-model="page"
            :length="totalPages"
            :total-visible="7"
            color="#f46523"
            @input="fetchBookings"
          ></v-pagination>
        </div>
      </div>
    </v-container>
  </div>
</template>

<script>
import { getUserBookings } from "../services/booking.service";
import GlobalLoader from "../components/GlobalLoader.vue";

export default {
  name: "MyBookingsView",
  components: {
    GlobalLoader,
  },
  data() {
    return {
      bookings: [],
      loading: false,
      page: 1,
      limit: 9,
      totalPages: 1,
    };
  },
  mounted() {
    this.fetchBookings();
  },
  computed: {
    isUSRoute() {
      return (
        this.$route.meta?.location === "us" ||
        this.$route.path.startsWith("/us")
      );
    },
    servicesPath() {
      return this.isUSRoute ? "/us/services" : "/services";
    },
  },
  methods: {
    goToBookingDetails(bookingId) {
      this.$router.push(
        this.isUSRoute ? `/us/booking/${bookingId}` : `/booking/${bookingId}`,
      );
    },
    async fetchBookings() {
      this.loading = true;
      try {
        const response = await getUserBookings(null, this.page, this.limit);
        
        const apiData = response.data?.data;
        if (apiData) {
          this.bookings = apiData.bookings || [];
          this.totalPages = apiData.totalPages || 1;
        } else {
          this.bookings = [];
          this.totalPages = 1;
        }
      } catch (error) {
        console.error("Error fetching bookings:", error);
        this.bookings = [];
      } finally {
        this.loading = false;
      }
    },
    getCartItem(booking) {
      return booking.cart_items?.[0] || {};
    },
    getAddress(booking) {
      return this.getCartItem(booking).details?.address || null;
    },
    formatDateTime(dateTimeString) {
      if (!dateTimeString) return "";
      const date = new Date(dateTimeString);
      return date.toLocaleString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    },
  },
};
</script>

<style scoped>
.my-bookings-page {
  padding-top: 40px;
  padding-bottom: 80px;
  background-color: #fcfcfc;
  min-height: calc(100vh - 80px);
}

.page-title {
  font-size: 36px;
  font-weight: 800;
  color: #1a1a1a;
  margin-bottom: 4px;
}

.page-subtitle {
  color: #666;
  font-size: 18px;
}

.primary-chip-active {
  background-color: #f46523 !important;
  color: white !important;
  border-color: #f46523 !important;
}

.filter-chip {
  text-transform: none;
  font-weight: 600;
  transition: all 0.2s;
}

.booking-card {
  border-radius: 16px !important;
  border: 1px solid #eee !important;
  transition: transform 0.2s, box-shadow 0.2s;
  position: relative;
  overflow: hidden;
  background: white !important;
  height: 100%;
}

.booking-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.05) !important;
}

.booking-status-badge {
  position: absolute;
  top: 16px;
  right: 16px;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  z-index: 1;
}

.booking-status-badge.booked,
.booking-status-badge.pending { background: #fff8e1; color: #ffa000; }
.booking-status-badge.confirmed { background: #e8f5e9; color: #43a047; }
.booking-status-badge.completed { background: #e3f2fd; color: #1e88e5; }
.booking-status-badge.cancelled { background: #ffebee; color: #e53935; }

.service-icon-wrapper {
  width: 56px;
  height: 56px;
  background: #fff3ed;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.service-details {
  max-width: calc(100% - 140px);
}

.booking-service-name {
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.3;
  margin-bottom: 2px;
}

.booking-id {
  font-size: 14px;
  color: #999;
  font-family: monospace;
}

.booking-info-row {
  display: flex;
  align-items: center;
  font-size: 16px;
  color: #444;
  margin-bottom: 12px;
}

.action-btn {
  font-weight: 700 !important;
  text-transform: none !important;
  font-size: 15px !important;
}

.status-pending {
  color: #ffa000;
  font-weight: 700;
}

.status-success {
  color: #43a047;
  font-weight: 700;
}

.address-container {
  display: flex;
  flex-direction: column;
}

.address-title {
  font-weight: 700;
  color: #333;
  font-size: 15px;
}

.address-text {
  color: #666;
  font-size: 15px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.empty-state {
  text-align: center;
  padding: 80px 20px;
}

.empty-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin-top: 16px;
}

.empty-text {
  color: #666;
  margin-top: 8px;
}

.pagination-wrapper {
  display: flex;
  justify-content: center;
}

@media (max-width: 600px) {
  .page-title {
    font-size: 28px;
  }

  .booking-service-name {
    font-size: 20px;
  }

  .booking-info-row {
    font-size: 15px;
  }

  .address-title,
  .address-text {
    font-size: 14px;
  }
}
</style>
